import { Box, Flex, Text } from "@chakra-ui/react";
import { Button } from "@euka-mfes/bark";
import impersonationApi from "@api/impersonationApi";
import { useState } from "react";
import { useUserSelector } from "@context/hooks";
import { useImpersonation } from "@hooks/useImpersonation";
import { logger } from "@utils/loggingUtils";

/**
 * ImpersonationBanner displays a red banner at the top of the Header when a
 * Portal admin is impersonating a student via the OEX two-step OAuth2 flow.
 *
 * On mount, it calls GET /euka-api/v1/impersonate/status (via the shared
 * `useImpersonation` hook — deduplicated across components). If the response
 * indicates an active impersonation session, it renders the banner with the
 * student's name and a "Stop Impersonating" button.
 *
 * The parent Header component uses a ResizeObserver to publish
 * `--euka-header-height` on `:root`, which automatically accounts for
 * this banner's height when it is visible.
 *
 * Clicking "Stop Impersonating" calls POST /euka-api/v1/impersonate/stop,
 * which destroys the OEX session and returns a redirect URL back to Portal Nova.
 */
const ImpersonationBanner = () => {
  const { isImpersonating } = useImpersonation();
  const [isStopping, setIsStopping] = useState(false);
  const { headerDetails } = useUserSelector();

  const studentName = headerDetails?.student?.first_name ?? "this student";

  const handleStop = async () => {
    setIsStopping(true);
    try {
      const result = await impersonationApi.stop();

      // Clear session storage before redirecting so the next user
      // gets a fresh MFE state.
      sessionStorage.clear();

      if (result.redirect) {
        window.location.href = result.redirect;
      }
    } catch (error) {
      logger.error("Failed to stop impersonation", {
        action: "impersonation_stop",
        error: String(error)
      });
      setIsStopping(false);
    }
  };

  if (!isImpersonating) {
    return null;
  }

  return (
    <Box
      bg="danger.600"
      color="base.200"
      px={4}
      py={1.5}
      display="flex"
      alignItems="center"
      data-testid="impersonation-banner"
      className="print-hide"
    >
      <Flex maxW="1140px" mx="auto" align="center" justify="center" gap={3}>
        <Text fontWeight="semibold" fontSize="xs">
          Impersonating as {studentName}
        </Text>
        <Button
          variant="tertiary"
          h="26px"
          py={0}
          onClick={handleStop}
          disabled={isStopping}
          data-testid="stop-impersonation-btn"
          data-tracking-id="stop-impersonation"
        >
          {isStopping ? "Stopping..." : "Stop Impersonating"}
        </Button>
      </Flex>
    </Box>
  );
};

export default ImpersonationBanner;
